import React, { useCallback, useEffect, useState } from 'react'
import {
  cardStyles,
  DragAndDropStack,
  MemberStaffCard,
  PersonSelectDialog,
  useGlobalMessageHandler,
  useUsLegRoleTypeLuts,
  useUsPrefixLuts
} from 'components'
import { BulkAssignButton } from 'components/us/bulk-assign/bulk-assign-button'
import {
  Box, Button,
  Card,
  CardContent, CircularProgress,
  Fab,
  Stack
} from '@mui/material'
import { useSessionContext } from 'session'
import {
  getLeadershipBasicInfo, type LeadershipOfficer,
  type PersonBaseBasicInformation
  , getStaffByLeadership, type LeadershipStaffInformation, updateLeadershipStafferDisplayOrder,
  getLeadershipFullInfo
} from 'services'
import { bulkAssignLeadershipOffice } from 'services/leadership/leadershipStaffService'
import { useNavigate, useParams } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import { generateLegalName, getEmptyPersonAddress, getEmptyStafferEmail, getEmptyPersonPhone } from 'helpers'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import { US_LEADERSHIP_OFFICER_ROLES, US_STAFFER_ROLES, PHONE_TYPES } from 'utils'

export const LeadershipStaffersPage: React.FC = () => {
  const { legOfcId } = useParams<{ legOfcId: string }>()
  const parsedLegOfcId = legOfcId != null ? parseInt(legOfcId, 10) : 0
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const [loading, setLoading] = useState(true)
  const { showErrorMessage } = useGlobalMessageHandler()
  const [leadership, setLeadership] = useState<LeadershipOfficer | null>()
  const navigate = useNavigate()

  const [reloadTrigger, setReloadTrigger] = useState(0)
  const [openStaffDialog, setOpenStaffDialog] = useState(false)

  const [staff, setStaff] = useState<LeadershipStaffInformation[]>([])
  const [leadershipAddress, setLeadershipAddress] = useState<any>(null)

  const [creatingStaff, setCreatingStaff] = useState<LeadershipStaffInformation | undefined>(undefined)
  const { lutValues: legRoleTypeLutValues, loading: loadingLegRoleTypeLutValues } = useUsLegRoleTypeLuts()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()

  useEffect(() => {
    const generateModuleName = (leadership: LeadershipOfficer | null | undefined): string => {
      if (leadership == null || leadership.person.id == null || leadership.state?.desc == null) return 'Leadership/Officer Staffers'
      return `Staffers for ${leadership.ofcname} (${leadership.legchamber.desc}${leadership.state.desc !== 'All'
          ? `, ${leadership.state.desc}`
          : ', U.S'
      }): ${generateLegalName(leadership.person as PersonBaseBasicInformation, usPrefixLutValues.prefixes)}`
    }

    changeModuleName(generateModuleName(leadership))
    changeUniqueId(`LegOfcID: ${parsedLegOfcId}`)
  }, [changeModuleName, leadership, usPrefixLutValues.prefixes])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true)
        const legOfcStaffInfoResponse = await getStaffByLeadership(parsedLegOfcId)
        const leadership = await getLeadershipBasicInfo(parsedLegOfcId)
        const leadershipWithAddress = await getLeadershipFullInfo(parsedLegOfcId)
        setLeadership(leadership.result)
        setLeadershipAddress(leadershipWithAddress.result?.address)
        setStaff(legOfcStaffInfoResponse)
      } catch (error: any) {
        let errorMessage = 'An unexpected error occurred during the request.'
        if (error.response?.status === 404 || error.response?.status === 400) {
          errorMessage = Object.values(error.response.data.errors).join('\n')
        }
        showErrorMessage(errorMessage)
      } finally {
        setLoading(false)
      }
    }
    void fetchData()
  }, [parsedLegOfcId])

  const addStaffer = (): void => {
    setOpenStaffDialog(true)
  }

  const getLegOfcStafferRole = (legofcRoleId: number): number => {
    switch (legofcRoleId) {
      case US_LEADERSHIP_OFFICER_ROLES.FEDERAL_OFFICER:
        return US_STAFFER_ROLES.FEDERAL_OFFICER_STAFF
      case US_LEADERSHIP_OFFICER_ROLES.FEDERAL_LEADERSHIP:
        return US_STAFFER_ROLES.FEDERAL_LEADERSHIP_STAFF
      case US_LEADERSHIP_OFFICER_ROLES.STATE_OFFICER:
        return US_STAFFER_ROLES.STATE_OFFICER_STAFF
      case US_LEADERSHIP_OFFICER_ROLES.STATE_LEADERSHIP:
        return US_STAFFER_ROLES.STATE_LEADERSHIP_STAFF
      default:
        return US_STAFFER_ROLES.FEDERAL_LEADERSHIP_STAFF
    }
  }

  const handlePersonSelected = (person: PersonBaseBasicInformation): void => {
    if (leadership == null) return
    setOpenStaffDialog(false)

    const emptyAddress = getEmptyPersonAddress(person.id ?? 0)

    const defaultPhone = getEmptyPersonPhone(person.id ?? 0, emptyAddress.id)
    defaultPhone.typeId = PHONE_TYPES.PHONE

    const defaultFax = getEmptyPersonPhone(person.id ?? 0, emptyAddress.id)
    defaultFax.typeId = PHONE_TYPES.FAX

    emptyAddress.phones = [defaultPhone, defaultFax]

    const newStaffer: LeadershipStaffInformation = {
      id: 0,
      person,
      role: { id: getLegOfcStafferRole(leadership.role.id), desc: '', code: '', branch: { id: leadership.role.branch.id, branch_desc: '' } },
      legrole: undefined,
      legrole2: undefined,
      legrole3: undefined,
      leadershipOfficer: leadership,
      statusId: undefined,
      title: undefined,
      email: getEmptyStafferEmail(person.id ?? 0),
      address: emptyAddress,
      displayOrder: 0,
      updatedBy: undefined,
      lastUpdated: undefined,
      responsibilities: []
    }
    setCreatingStaff(newStaffer)
  }

  const handleStaffRetire = (retiredStaff: LeadershipStaffInformation): void => {
    setStaff(prevStaff => prevStaff.filter(staff => staff.id !== retiredStaff.id))
    setReloadTrigger(prev => prev + 1)
  }

  const refreshStaff = useCallback(async (): Promise<void> => {
    try {
      const refreshed = await getStaffByLeadership(parsedLegOfcId)
      setStaff(refreshed)
      setReloadTrigger(prev => prev + 1)
    } catch (error: any) {
      showErrorMessage('Failed to refresh staffers after bulk-assign.')
    }
  }, [parsedLegOfcId, showErrorMessage])

  const handleBulkAssignSuccess = useCallback(async (): Promise<void> => {
    await refreshStaff()
  }, [refreshStaff])

  const ownerDisplayName = leadership?.person != null
    ? generateLegalName(leadership.person as PersonBaseBasicInformation, usPrefixLutValues.prefixes)
    : (leadership?.ofcname ?? 'this office')

  const renderStafferCardsByType = (staffers: LeadershipStaffInformation[]): React.ReactNode => {
    if (staffers == null) return null

    const sortedStaff = staffers.sort((a, b) => {
      const displayOrderA = a.displayOrder ?? 0
      const displayOrderB = b.displayOrder ?? 0
      return displayOrderA - displayOrderB
    })

    const staffersByRoleMap = sortedStaff.reduce((map, staffer) => {
      const roleId = staffer.legrole?.id ?? 0
      if (map.get(roleId) == null) {
        map.set(roleId, [])
      }
      map.get(roleId)?.push(staffer)
      return map
    }, new Map<number, LeadershipStaffInformation[]>())

    const sortedStaffersByRole = Array.from(staffersByRoleMap.entries())
      .sort(([_, staffersA], [__, staffersB]) => {
        const lutOrderA = staffersA[0]?.legrole?.lutOrder ?? 0
        const lutOrderB = staffersB[0]?.legrole?.lutOrder ?? 0
        return lutOrderA - lutOrderB
      })

    const groupedStaffList = (
      <Stack spacing={2}>
        {sortedStaffersByRole.flatMap(([roleId, staffers]) => (
          <DragAndDropStack
            key={roleId}
            id={`staff-${parsedLegOfcId ?? ''}-${roleId}`}
            originalItems={staffers}
            itemRenderer={(item) => (
              <MemberStaffCard
                key={item.id}
                createCard={false}
                originalPosition={item}
                legRoleLuts={legRoleTypeLutValues}
                loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                onStaffUpdate={(updatedStaff) => { handleStaffUpdate(updatedStaff) }}
                onRetire={(retiredStaff) => { handleStaffRetire(retiredStaff) }}
                leadershipAddress={leadershipAddress}
              />
            )}
            updateDisplayOrder={async (items, itemSelected) => {
              const displayOrderById = items.reduce<Record<number, number>>((acc, staff, index) => ({ ...acc, [staff.id]: index + 1 }), {})
              const response = await updateLeadershipStafferDisplayOrder(parsedLegOfcId, displayOrderById, itemSelected.id, itemSelected.legrole.id)
              if (response.succeeded) {
                setStaff(prevStaff =>
                  [...prevStaff.map(staff =>
                    displayOrderById[staff.id] != null ? { ...staff, displayOrder: displayOrderById[staff.id] } : staff
                  )]
                )
              }
              return response
            }}
            disableDrag={loading || staffers.length <= 1}
            makeGray={false}
          />
        ))}
      </Stack>
    )

    return groupedStaffList
  }

  const handleStaffUpdate = (updatedStaff: LeadershipStaffInformation): void => {
    setStaff(prevStaff => prevStaff.map(staff => staff.id === updatedStaff.id ? updatedStaff : staff))
    setReloadTrigger(prev => prev + 1)
  }

  const memoizedStafferCardsByType = React.useMemo(
    () => renderStafferCardsByType(staff),
    [staff, legOfcId, reloadTrigger]
  )

  const addToExistingStaffers = (newStaffer: LeadershipStaffInformation): void => {
    setStaff(prevStaff => [...prevStaff, newStaffer])
    setReloadTrigger(prev => prev + 1)
  }

  if (staff.length === 0 && loading) return <Box display="flex" height="90vh" alignItems="center" justifyContent="center"><CircularProgress/></Box>

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 1
        }}
      >
        <Stack spacing={2} alignItems="center" mt={1} width="98%">
          <Box id='titleAdd' sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button variant="contained" size='large' onClick={() => { navigate(`/us/leadershipOfficers/edit/${parsedLegOfcId}`) }}><ArrowBackIosNewRoundedIcon/></Button>
            <BulkAssignButton
              ownerDisplayName={ownerDisplayName}
              targetLabel="office"
              buttonLabel="Apply office contact info to all staffers"
              dialogTitle="Bulk-assign office contact info"
              staffers={staff}
              hasSourceAddress={leadershipAddress?.id != null}
              onConfirm={async () => await bulkAssignLeadershipOffice(parsedLegOfcId)}
              onSuccess={handleBulkAssignSuccess}
            />
          </Box>
          <Card sx={cardStyles}>
                <CardContent>
                  {!loading &&
                    <>
                      {creatingStaff == null &&
                      <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                        <Fab color="primary" aria-label="add" onClick={() => { addStaffer() } }>
                          <AddIcon />
                        </Fab>
                      </Box>
                      }
                      {memoizedStafferCardsByType}
                      {creatingStaff != null && (
                        <Box mt={2}>
                          <MemberStaffCard
                            key="new-staff-card"
                            createCard={true}
                            originalPosition={creatingStaff}
                            legRoleLuts={legRoleTypeLutValues}
                            loadingLegRoleTypeLutValues={loadingLegRoleTypeLutValues}
                            onStaffUpdate={setCreatingStaff}
                            cancelCreate={() => { setCreatingStaff(undefined) }}
                            addToExistingStaffers={(createdStaff) => { addToExistingStaffers(createdStaff) }}
                            leadershipAddress={leadershipAddress}
                          />
                        </Box>
                      )}
                    </>
                  }
                </CardContent>
          </Card>
        </Stack>
      </Box>
      <PersonSelectDialog<PersonBaseBasicInformation>
        open={openStaffDialog}
        onClose={() => { setOpenStaffDialog(false) }}
        onPersonSelected={handlePersonSelected}
        title="Select Person"
        personType="personUS"
        fullWidth={true}
        maxWidth="md"
        minHeight="60vh"
      />
    </>
  )
}
